import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  useIsMobile,
  type DateRange,
} from "@base/ui";
import { startOfDay } from "date-fns";
import { CalendarDays, ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  PERIODOS_PRONTOS,
  descreverIntervalo,
  identificarPeriodoPronto,
  periodoPronto,
  type Intervalo,
} from "./periodos";

/**
 * Seletor de periodo: atalhos prontos a esquerda, calendario de dois meses a
 * direita, e o filtro so muda ao clicar em Aplicar.
 *
 * Por que rascunho + Aplicar: no modo intervalo o primeiro clique ja e uma data
 * valida. Sem o rascunho, o dashboard recarregaria no meio da escolha, com um
 * periodo de um dia so.
 */
export function SeletorDePeriodo({
  valor,
  aoMudar,
  hoje = startOfDay(new Date()),
}: {
  readonly valor: Intervalo;
  readonly aoMudar: (intervalo: Intervalo) => void;
  /** Injetavel para teste. */
  readonly hoje?: Date;
}) {
  const celular = useIsMobile();
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState<DateRange | undefined>({ from: valor.inicio, to: valor.fim });

  const prontoAtual = identificarPeriodoPronto(valor, hoje);
  const intervaloDoRascunho: Intervalo | null = rascunho?.from
    ? { inicio: rascunho.from, fim: rascunho.to ?? rascunho.from }
    : null;
  const prontoDoRascunho = intervaloDoRascunho ? identificarPeriodoPronto(intervaloDoRascunho, hoje) : null;

  function abrirOuFechar(abrir: boolean) {
    if (abrir) setRascunho({ from: valor.inicio, to: valor.fim });
    setAberto(abrir);
  }

  function aplicar() {
    if (!intervaloDoRascunho) return;
    aoMudar(intervaloDoRascunho);
    setAberto(false);
  }

  return (
    <Popover open={aberto} onOpenChange={abrirOuFechar}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-11 gap-3 rounded-full border-border bg-card px-4 shadow-sm"
          aria-label={`Período: ${prontoAtual?.rotulo ?? descreverIntervalo(valor)}. Trocar período`}
        >
          <CalendarDays className="text-primary" aria-hidden="true" />
          <span className="flex items-baseline gap-2">
            {prontoAtual ? <span className="font-semibold">{prontoAtual.rotulo}</span> : null}
            <span className={cn("tabular-nums", prontoAtual && "text-muted-foreground")}>
              {descreverIntervalo(valor)}
            </span>
          </span>
          <ChevronDown className="opacity-60" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-auto rounded-3xl p-0 shadow-xl">
        <div className="flex flex-col md:flex-row">
          <div
            role="listbox"
            aria-label="Períodos prontos"
            className="flex gap-1 overflow-x-auto border-b p-3 md:w-48 md:flex-col md:overflow-visible md:border-r md:border-b-0"
          >
            {PERIODOS_PRONTOS.map((pronto) => {
              const ativo = prontoDoRascunho?.id === pronto.id;
              return (
                <button
                  key={pronto.id}
                  type="button"
                  role="option"
                  aria-selected={ativo}
                  onClick={() => {
                    const intervalo = periodoPronto(pronto.id, hoje);
                    setRascunho({ from: intervalo.inicio, to: intervalo.fim });
                  }}
                  className={cn(
                    "shrink-0 rounded-full px-3 py-2 text-left text-sm whitespace-nowrap transition-colors",
                    ativo
                      ? "bg-secondary font-medium text-secondary-foreground"
                      : "text-foreground hover:bg-muted",
                  )}
                >
                  {pronto.rotulo}
                </button>
              );
            })}
          </div>
          <div className="flex flex-col">
            <Calendar
              mode="range"
              selected={rascunho}
              onSelect={setRascunho}
              numberOfMonths={celular ? 1 : 2}
              defaultMonth={celular ? valor.fim : addMeses(valor.fim, -1)}
              disabled={{ after: hoje }}
              className="p-4"
            />
            <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground tabular-nums" aria-live="polite">
                {intervaloDoRascunho
                  ? descreverIntervalo(intervaloDoRascunho)
                  : "Escolha a data de início"}
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setAberto(false)}>
                  Cancelar
                </Button>
                <Button onClick={aplicar} disabled={!intervaloDoRascunho}>
                  Aplicar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function addMeses(data: Date, meses: number): Date {
  return new Date(data.getFullYear(), data.getMonth() + meses, 1);
}
